import { useState, useEffect } from "react";
import api from "../services/api";
import "./FileConvertor.css";

const Usage = () => {
  // ===== STATE MANAGEMENT =====
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ===== EFFECTS =====
  useEffect(() => {
    fetchUsageData();
  }, []);

  // ===== API CALLS =====
  const fetchUsageData = async () => {
    try {
      setLoading(true);
      const [usageRes, historyRes] = await Promise.all([
        api.get("/usage"),
        api.get("/usage/history"),
      ]);

      setUsage(usageRes.data.data);
      setHistory(historyRes.data.data.history);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch usage data");
    } finally {
      setLoading(false);
    }
  };

  // ===== UTILITY FUNCTIONS =====
  const getMonthName = (month) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[month - 1];
  };

  const calculatePercentage = (used, total) => {
    if (!total || total === 0) return 0;
    return Math.min((used / total) * 100, 100);
  };

  const getPlanBadgeClass = (plan) => {
    const planLower = plan?.toLowerCase();
    if (planLower === "unlimited") return "usage-plan-badge-premium";
    if (planLower === "pro") return "usage-plan-badge-pro";
    if (planLower === "premium") return "usage-plan-badge-premium";
    return "usage-plan-badge-free";
  };

  // ===== RENDER FUNCTIONS =====
  const renderHeader = () => (
    <div className="usage-page-header">
      <h1 className="display-4 fw-bold mb-3 usage-page-title">
        <i className="fa fa-line-chart me-3"></i>
        Usage Statistics
      </h1>
      <p className="lead text-muted" style={{ maxWidth: "750px" }}>
        Monitor your conversion activity, track credit usage, and analyze your monthly trends
      </p>
    </div>
  );

  const renderStatCards = () => {
    const currentMonthCredits = usage?.currentMonth?.creditsUsed || 0;
    const currentMonthConversions = usage?.currentMonth?.conversions || 0;
    const remainingCredits = usage?.subscription?.remainingCredits || 0;
    const totalCreditsUsed = usage?.allTime?.creditsUsed || 0;
    const totalConversions = usage?.allTime?.conversions || 0;

    const stats = [
      {
        label: "Current Month Credits",
        value: currentMonthCredits,
        icon: "fa-credit-card",
        type: "primary",
      },
      {
        label: "Current Month Conversions",
        value: currentMonthConversions,
        icon: "fa-exchange",
        type: "success",
      },
      {
        label: "Remaining Credits",
        value: remainingCredits,
        icon: "fa-battery-three-quarters",
        type: "info",
      },
      {
        label: "Total Credits Used",
        value: totalCreditsUsed,
        icon: "fa-pie-chart",
        type: "warning",
      },
      {
        label: "Total Conversions",
        value: totalConversions,
        icon: "fa-check-circle",
        type: "success",
      },
    ];

    return (
      <div className="row g-4 mb-5">
        {stats.map((stat, index) => (
          <div key={index} className="col-md-6 col-lg-4">
            <div className={`usage-stat-card usage-stat-card-${stat.type}`}>
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <p className="usage-stat-label">{stat.label}</p>
                  <h2 className="usage-stat-value">{stat.value}</h2>
                </div>
                <div className={`usage-stat-icon usage-stat-icon-${stat.type}`}>
                  <i className={`fa ${stat.icon}`}></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSubscriptionInfo = () => {
    const plan = usage?.subscription?.plan || "Free";
    const remainingCredits = usage?.subscription?.remainingCredits || 0;
    const totalCredits = usage?.subscription?.totalCredits || 100;
    const percentage = calculatePercentage(
      totalCredits - remainingCredits,
      totalCredits
    );

    return (
      <div className="usage-info-card usage-slide-up mb-5">
        <div className="usage-info-card-header">
          <h5 className="usage-info-card-title d-flex align-items-center">
            <i className="fa fa-info-circle me-2" style={{ color: "#6366f1" }}></i>
            Subscription Information
          </h5>
        </div>
        <div className="card-body p-4">
          <div className="row g-4">
            <div className="col-md-4">
              <div className="usage-subscription-item">
                <p className="text-muted mb-2 small fw-semibold">Current Plan</p>
                <span className={`usage-plan-badge ${getPlanBadgeClass(plan)}`}>
                  {plan}
                </span>
              </div>
            </div>
            <div className="col-md-4">
              <div className="usage-subscription-item">
                <p className="text-muted mb-2 small fw-semibold">Credits Status</p>
                <h5 className="fw-bold mb-2 text-dark">
                  {remainingCredits} / {totalCredits}
                </h5>
                <div className="usage-progress-bar">
                  <div
                    className="usage-progress-fill usage-progress-primary"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center h-100">
                <a
                  href="/billing"
                  className="usage-upgrade-btn w-100 text-center text-decoration-none"
                >
                  <i className="fa fa-rocket me-2"></i>
                  Upgrade Plan
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUsageHistory = () => (
    <div className="usage-info-card usage-slide-up mb-4">
      <div className="usage-info-card-header">
        <h5 className="usage-info-card-title d-flex align-items-center">
          <i className="fa fa-history me-2" style={{ color: "#6366f1" }}></i>
          Monthly Usage History
        </h5>
      </div>
      <div className="card-body p-0">
        {history.length === 0 ? (
          <div className="usage-empty-state">
            <i className="fa fa-inbox fa-5x usage-empty-icon d-block"></i>
            <h5 className="text-muted fw-bold mb-2">No usage history available</h5>
            <p className="text-muted mb-0">
              Your monthly usage history will appear here once you start converting files
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="usage-history-table">
              <thead>
                <tr>
                  <th>
                    <i className="fa fa-calendar me-2"></i>Month
                  </th>
                  <th>
                    <i className="fa fa-credit-card me-2"></i>Credits Used
                  </th>
                  <th>
                    <i className="fa fa-exchange me-2"></i>Conversions
                  </th>
                  <th>
                    <i className="fa fa-calculator me-2"></i>Avg. Credits
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((record, index) => (
                  <tr key={index}>
                    <td className="fw-bold">
                      {getMonthName(record._id.month)} {record._id.year}
                    </td>
                    <td>
                      <span className="usage-badge usage-badge-primary">
                        {record.creditsUsed}
                      </span>
                    </td>
                    <td>
                      <span className="usage-badge usage-badge-success">
                        {record.conversions}
                      </span>
                    </td>
                    <td>
                      <span className="usage-badge usage-badge-warning">
                        {record.conversions > 0
                          ? (record.creditsUsed / record.conversions).toFixed(2)
                          : "0.00"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderTips = () => (
    <div className="usage-tips-card">
      <h6 className="d-flex align-items-center mb-3">
        <i className="fa fa-lightbulb-o fa-lg me-2"></i>
        Pro Tips for Optimizing Your Usage
      </h6>
      <ul>
        <li>
          <strong>Credits reset monthly</strong> based on your subscription plan - make sure to use them before the end of the month
        </li>
        <li>
          <strong>Upgrade to a premium plan</strong> for more credits, faster processing, and priority support
        </li>
        <li>
          <strong>Monitor your usage patterns</strong> to choose the most cost-effective plan for your needs
        </li>
        <li>
          <strong>Batch conversions</strong> during off-peak hours for optimal performance
        </li>
      </ul>
    </div>
  );

  // ===== LOADING STATE =====
  if (loading) {
    return (
      <div className="usage-main-wrapper">
        <div className="usage-loading-wrapper">
          <div className="text-center">
            <div className="usage-spinner mx-auto mb-3"></div>
            <p className="text-muted fw-semibold">Loading usage statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  // ===== ERROR STATE =====
  if (error) {
    return (
      <div className="usage-main-wrapper">
        <div className="container py-5">
          <div className="files-alert-modern files-alert-danger">
            <div className="d-flex align-items-center">
              <i className="fa fa-exclamation-triangle fa-lg me-3"></i>
              <div>
                <strong className="d-block mb-1">Error Loading Data</strong>
                <span>{error}</span>
              </div>
            </div>
          </div>
          <div className="text-center mt-4">
            <button
              className="usage-upgrade-btn"
              onClick={fetchUsageData}
            >
              <i className="fa fa-refresh me-2"></i>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== MAIN RENDER =====
  return (
    <div className="usage-main-wrapper">
      <div className="container py-5">
        {renderHeader()}
        {renderStatCards()}
        {renderSubscriptionInfo()}
        {renderUsageHistory()}
        {renderTips()}
      </div>
    </div>
  );
};

export default Usage;
